import * as React from "react"
import { Navigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { useAuthStore } from "@/stores/auth.store"

// Step 1: Email only for login
const loginSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
})

// Step 2: OTP Code
const otpSchema = z.object({
  otp: z.string().min(1, "OTP is required").max(6, "OTP must be 6 digits").regex(/^\d*$/, "OTP must contain only numbers"),
})

type LoginFormData = z.infer<typeof loginSchema>
type OTPFormData = z.infer<typeof otpSchema>

export const LoginPage: React.FC = () => {
  const { login: _login, isLoading, error, isAuthenticated } = useAuthStore()
  const [step, setStep] = React.useState<'credentials' | 'otp'>('credentials')
  const [credentials, setCredentials] = React.useState<LoginFormData | null>(null)
  const [isRequestingOTP, setIsRequestingOTP] = React.useState(false)

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
    },
  })

  const otpForm = useForm<OTPFormData>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      otp: "",
    },
  })

  const [otpValue, setOtpValue] = React.useState("")

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  const onSubmitCredentials = async (data: LoginFormData) => {
    setIsRequestingOTP(true)
    try {
      // Send request to login endpoint to trigger OTP email
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to send OTP' }))
        throw new Error(errorData.error || 'Failed to send OTP')
      }

      // Store credentials for later use
      setCredentials(data)
      // Move to OTP step
      setStep('otp')
    } catch (error: any) {
      loginForm.setError('root', {
        message: error.message || 'Failed to send OTP code. Please try again.'
      })
    } finally {
      setIsRequestingOTP(false)
    }
  }

  const onSubmitOTP = async (data: OTPFormData) => {
    if (!credentials) return

    try {
      // Call the verify-login endpoint with email and OTP
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/verify-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies if needed
        body: JSON.stringify({
          email: credentials.email,
          otp: data.otp,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Verification failed' }))
        throw new Error(errorData.error || 'Invalid OTP code')
      }

      const authData = await response.json()

      // Update auth store with the token and user data
      if (authData.accessToken && authData.user) {
        // Use the store's setter methods properly
        const authStore = useAuthStore.getState()

        // Update the store state
        authStore.setTokens(authData.accessToken, authData.refreshToken)

        // Use setState to update multiple values at once
        useAuthStore.setState({
          user: authData.user,
          accessToken: authData.accessToken,
          refreshToken: authData.refreshToken || null,
          isAuthenticated: true,
          isLoading: false,
          error: null
        })

        // Don't use window.location.href as it causes a full page reload
        // The component will re-render and Navigate will handle the redirect
      }
    } catch (error: any) {
      otpForm.setError('root', {
        message: error.message || 'Invalid OTP code. Please try again.'
      })
    }
  }

  const handleResendOTP = async () => {
    if (!credentials) return

    setIsRequestingOTP(true)
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      })

      if (!response.ok) {
        throw new Error('Failed to resend OTP')
      }

      otpForm.clearErrors()
      otpForm.reset()
      // Show success message (you might want to add a toast notification here)
    } catch (error: any) {
      otpForm.setError('root', {
        message: 'Failed to resend OTP. Please try again.'
      })
    } finally {
      setIsRequestingOTP(false)
    }
  }

  const handleBackToCredentials = () => {
    setStep('credentials')
    setOtpValue('')
    otpForm.reset()
    otpForm.clearErrors()
  }

  if (step === 'otp') {
    return (
      <Card>
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl">Enter verification code</CardTitle>
          <CardDescription>
            We've sent a 6-digit code to {credentials?.email}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...otpForm}>
            <form onSubmit={otpForm.handleSubmit(onSubmitOTP)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">Verification Code</Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="123456"
                  maxLength={6}
                  className="text-center text-2xl tracking-widest"
                  autoComplete="one-time-code"
                  value={otpValue}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setOtpValue(value);
                    otpForm.setValue('otp', value);
                  }}
                />
                {otpForm.formState.errors.otp && (
                  <p className="text-sm text-destructive">
                    {otpForm.formState.errors.otp.message}
                  </p>
                )}
              </div>
              {otpForm.formState.errors.root && (
                <div className="text-sm text-destructive">
                  {otpForm.formState.errors.root.message}
                </div>
              )}
              {error && (
                <div className="text-sm text-destructive">{error}</div>
              )}
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Verifying..." : "Verify and Sign In"}
              </Button>
              <div className="flex justify-between">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToCredentials}
                >
                  ← Back
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleResendOTP}
                  disabled={isRequestingOTP}
                >
                  {isRequestingOTP ? "Sending..." : "Resend Code"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl">Sign in</CardTitle>
        <CardDescription>
          Enter your email to receive a verification code
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...loginForm}>
          <form onSubmit={loginForm.handleSubmit(onSubmitCredentials)} className="space-y-4">
            <FormField
              control={loginForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="m@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {loginForm.formState.errors.root && (
              <div className="text-sm text-destructive">
                {loginForm.formState.errors.root.message}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={isRequestingOTP}>
              {isRequestingOTP ? "Sending code..." : "Send Verification Code"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}